import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Home, Settings, LogOut, ChevronDown, User, Menu, X } from "lucide-react";
import AmmoLogo from "./AmmoLogo";
import NotificationBell from "./NotificationBell";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { to: "/", testId: "nav-home", icon: Home, label: t("nav.home"), exact: true },
    ...(user?.role === "admin" ? [
      { to: "/settings", testId: "nav-settings", icon: Settings, label: t("nav.settings") },
    ] : []),
  ];

  return (
    <nav className="sticky top-0 z-50 navbar-glass border-b border-slate-200/50">
      {/* In a browser tab or on a phone this is 0px and invisible; see
          .titlebar-extension in index.css. In the installed desktop app it
          adds the height that content is centred within — see navbar-row
          below, which is the element that actually grows. This div exists so
          the extra space still counts as part of the coloured bar even though
          nothing is centred inside it specifically. */}
      <div className="titlebar-extension" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* navbar-row is what actually gains height in the installed desktop
            app — see index.css. Growing this row rather than only the strip
            above it means items-center centres the logo, links and menus in
            the middle of the whole bar, not in the lower half of it. */}
        <div className="navbar-row flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link">
              <AmmoLogo className="w-8 h-8 sm:w-10 sm:h-10" />
              <span className="font-semibold text-slate-800 hidden sm:block">
                AMMO Core
              </span>
            </Link>
          </div>

          {/* Desktop Nav Links — hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  (link.exact ? isActive("/") && location.pathname === "/" : isActive(link.to))
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-600 hover:text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Notifications + Language + User Menu + hamburger */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* The bell stays outside the mobile menu: it carries a count, and
                a count hidden behind a hamburger is a count nobody sees. */}
            <NotificationBell />

            {/* Language Switcher. Both buttons are a fixed width rather than
                sized to their own label: "EN" and "IT" are the same length but
                not the same visual width — E and N are wide letters, I and T
                are narrow — so padding sized to content left them visibly
                uneven. A fixed width makes them identical regardless of which
                two letters are in them. */}
            <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden" data-testid="language-switcher">
              <button
                onClick={() => setLanguage("en")}
                data-testid="lang-en"
                className={`w-8 py-1.5 text-xs font-medium text-center transition-colors ${
                  language === "en" ? "bg-indigo-600 text-white" : "bg-white text-slate-500 hover:text-indigo-600"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage("it")}
                data-testid="lang-it"
                className={`w-8 py-1.5 text-xs font-medium text-center transition-colors ${
                  language === "it" ? "bg-indigo-600 text-white" : "bg-white text-slate-500 hover:text-indigo-600"
                }`}
              >
                IT
              </button>
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-1 sm:gap-2 text-slate-700 hover:text-indigo-700 px-1.5 sm:px-3"
                >
                  <div 
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white font-medium text-xs sm:text-sm"
                    style={{ backgroundColor: user?.color || '#3730A3' }}
                  >
                    {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">
                    {user?.display_name}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-900">{user?.display_name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <p className="text-xs text-indigo-600 mt-1 capitalize">{user?.role === "admin" ? t("users.admin") : t("users.userRole")}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="nav-profile">
                    <User className="w-4 h-4" />
                    {t("nav.profile")}
                  </Link>
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        {t("nav.settings")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="flex items-center gap-2 text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  {t("nav.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile hamburger — visible only on small screens */}
            <button
              className="md:hidden p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200/50 py-2 -mx-4 px-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-${link.testId}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  (link.exact ? isActive("/") && location.pathname === "/" : isActive(link.to))
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-600 hover:text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
